import { computed, type Ref } from 'vue'

/**
 * Live password-complexity state, shared by every screen that sets a password
 * (reset flow, credentials change). The rules mirror `UserPasswordValidator`
 * on the backend exactly (length + lowercase + uppercase + digit + special),
 * plus the confirmation match.
 */

/** Minimum length — mirrors `User::PASSWORD_MIN_LENGTH` on the backend. */
export const PASSWORD_MIN_LENGTH = 12

export type PasswordRule = { key: string; met: boolean }

export function usePasswordRules(password: Ref<string>, confirmation: Ref<string>) {
  /** The 5 backend complexity rules (mirror of UserPasswordValidator). */
  const rules = computed<PasswordRule[]>(() => {
    const value = password.value
    return [
      { key: 'length', met: value.length >= PASSWORD_MIN_LENGTH },
      { key: 'lowercase', met: /[a-z]/.test(value) },
      { key: 'uppercase', met: /[A-Z]/.test(value) },
      { key: 'digit', met: /\d/.test(value) },
      { key: 'special', met: /[^A-Za-z0-9]/.test(value) },
    ]
  })

  const allRulesMet = computed(() => rules.value.every((rule) => rule.met))
  const passwordsMatch = computed(
    () => password.value.length > 0 && password.value === confirmation.value,
  )

  /** What the checklist UI renders: complexity rules + the match line. */
  const checklist = computed<PasswordRule[]>(() => [
    ...rules.value,
    { key: 'match', met: passwordsMatch.value },
  ])

  /** True when the password is strong AND confirmed. */
  const canSubmit = computed(() => allRulesMet.value && passwordsMatch.value)

  return { rules, checklist, allRulesMet, passwordsMatch, canSubmit }
}
